// @ts-nocheck
import React, { useState, useEffect, useContext } from 'react';
import { colors, layout } from '@/theme';
import Fetch from '@/utils/Fetch';
import ToastContext from '@/utils/toast-context';
import { useRouter } from 'next/router';
import { useQuery } from '@tanstack/react-query';
import Helper from '@/utils/helper';
import AppointmentCard from '@/components/customer-zone/appointment/appointment-card';
import PageContext from '@/utils/page-context';
import SCListCardSection from '@/components/sc-controls/layout/sc-list-card-section';
import useMobileView from '@/hooks/useMobileView';
import Time from '@/utils/time';
import NoSSR from '@/utils/no-ssr';
import LeadMessage from '@/components/customer-zone/general/lead-message';
import { createFileRoute } from '@tanstack/react-router'

const pageSize = 10;

function AppointmentList() {
  const toast = useContext(ToastContext);
  const pageContext = useContext(PageContext);
  const [mobileView] = useMobileView();
  const router = useRouter();
  const { t, c } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

  const { data: tenantData } = useQuery(
    ['czTenant', t],
    () => Fetch.preLogin({ tenantID: t }),
    { enabled: !!t }
  );
  const tenant = tenantData?.HttpStatusCode === 200 && tenantData.Results?.length > 0 ? tenantData.Results[0] : null;

  const { data: nowData } = useQuery(
    ['czNow', t, c],
    () => Time.nowCZ(t, c, tenant.API),
    { enabled: !!tenant }
  );
  const todayFilter = nowData ? Time.today((nowData as any)) : null;

  const { data: appointmentsData } = useQuery(
    ['czAppointments', t, c, todayFilter],
    () => Fetch.post({
      url: '/CustomerZone/GetAppointments',
      params: {
        PageSize: pageSize,
        SortExpression: "StartDateTime",
        SortDirection: "ascending",
        StartDateTime: todayFilter
      },
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    }),
    { enabled: !!tenant && !!todayFilter }
  );

  const [appointmentResults, setAppointmentResults] = useState<any>(null);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [todaysAppointments, setTodaysAppointments] = useState<any>(null);
  const [futureAppointments, setFutureAppointments] = useState<any>(null);
  const [searching, setSearching] = useState(false);

  const getTodaysAppointments = (appointments: any, filter: any) => {
    let today = Time.parseDate(filter);
    let tomorrow = Time.parseDate(filter);
    tomorrow.setDate(tomorrow.getDate() + 1);
    let todaysApps = appointments.filter((x: any) => Time.parseDate(x.StartDateTime).valueOf() >= today.valueOf() && Time.parseDate(x.StartDateTime).valueOf() < tomorrow.valueOf());
    return todaysApps && todaysApps.length > 0 ? todaysApps : [];
  };

  const getFutureAppointments = (appointments: any, filter: any) => {
    let tomorrow = Time.parseDate(filter);
    tomorrow.setDate(tomorrow.getDate() + 1);
    let futureApps = appointments.filter((x: any) => Time.parseDate(x.StartDateTime).valueOf() >= tomorrow.valueOf());
    return futureApps && futureApps.length > 0 ? futureApps : [];
  };

  useEffect(() => {
    if (tenantData && !tenant) { router.replace('/'); }
  }, [tenantData]);

  useEffect(() => {
    if (appointmentsData?.headerTenantName) {
      (pageContext as any).updateTenantName(appointmentsData.headerTenantName);
    }
  }, [appointmentsData]);

  useEffect(() => {
    if (appointmentsData && !appointmentResults) {
      setAppointmentResults(appointmentsData.Results);
      setTotalResults(appointmentsData.TotalResults);
      setTodaysAppointments(getTodaysAppointments(appointmentsData.Results, todayFilter));
      setFutureAppointments(getFutureAppointments(appointmentsData.Results, todayFilter));
    }
  }, [appointmentsData]);

  useEffect(() => {
    if (currentPage > 0)
      searchAppointments(currentPage, appointmentResults, setAppointmentResults, setTotalResults);
  }, [currentPage]);

  async function searchAppointments(searchCurrentPage: any, searchAppointmentResults: any, searchSetAppointmentResults: any, searchSetTotalResults: any) {
    setSearching(true);

    const searchRes = await Fetch.post({
      url: '/CustomerZone/GetAppointments',
      params: {
        pageIndex: searchCurrentPage,
        PageSize: pageSize,
        SortExpression: "StartDateTime",
        SortDirection: "ascending",
        StartDateTime: todayFilter
      },
      toastCtx: toast,
      tenantID: t,
      customerID: c,
      apiUrlOverride: tenant.API
    });

    if (searchCurrentPage < 1) {
      searchSetAppointmentResults(searchRes.Results);
      searchSetTotalResults(searchRes.TotalResults);
      setTodaysAppointments(getTodaysAppointments(searchRes.Results, todayFilter));
      setFutureAppointments(getFutureAppointments(searchRes.Results, todayFilter));
    } else {
      let temp = [...searchAppointmentResults];
      temp.push(...searchRes.Results);
      searchSetAppointmentResults(temp);
      searchSetTotalResults(searchRes.TotalResults);
      setTodaysAppointments(getTodaysAppointments(temp, todayFilter));
      setFutureAppointments(getFutureAppointments(temp, todayFilter));
    }
    setSearching(false);
  }

  function appointmentClick(appointment: any) {
    Helper.nextRouter(router.push, `/customerzone/viewappointment?t=${t}&c=${c}&i=${appointment.ID}`);
  }

  if (!appointmentResults) return null;

  return (
    <NoSSR>
      <div className={mobileView ? "" : "centered"}
           style={{
             paddingInlineEnd: 'var(--mantine-spacing-sm)',
             minHeight: 'calc(100dvh - 20px)'
           }}
      >
        <LeadMessage customerID={c} tenantID={t} page={"customerzone/appointmentlist"} />
        <h2 className="page-title">Your Appointments</h2>

        <div className={"loading-overlay" + (searching ? " loading-overlay-visible" : "")}>
          <div className="loader"></div>
        </div>

        {todaysAppointments && todaysAppointments.length > 0 ?
          <>
            <SCListCardSection text="Today" colour={"Green"} />
            {todaysAppointments.map((appointment: any, key: any) => {
              return <AppointmentCard key={key} appointment={appointment} onClick={(a: any) => appointmentClick(a)} />
            })}
          </> : ""}

        {futureAppointments && futureAppointments.length > 0 ? <>
          <SCListCardSection text="Future" colour={"Blue"} />
          {futureAppointments.map((appointment: any, key: any) => {
            return <AppointmentCard key={key} appointment={appointment} onClick={(a: any) => appointmentClick(a)} />
          })}
        </> : ""}

        {appointmentResults.length < totalResults ?
          <div className="load-more" onClick={() => setCurrentPage(currentPage + 1)}>Load more</div>
          : ""}

        {!appointmentResults || appointmentResults.length === 0 ? <>
          <h4>There are no appointments</h4>
        </> : ""}

        <style jsx>{`
        .load-more {
            margin: 0.5rem;
            cursor: pointer;
            font-weight: bold;
            text-align: right;
            ${mobileView ? "" : `max-width: ${layout.listCardWidth};`}
        }

        .load-more:hover {
            text-decoration: underline;
        }

        h2.page-title {
          margin: 0;
          font-size: 1.5rem;
        }

        .container {
          box-sizing: border-box;
          display: flex;
          flex-direction: column;
          padding: 1.5rem 3rem;
          overflow-x: hidden;
        }
        .column {
          width: 100%;
        }
        .column-margin {
          margin-left: 24px;
        }
        .button-container {
          flex-shrink: 0;
          width: 9rem;
        }
        .button-container :global(.button){
          margin-top: 0.5rem;
        }
        .title {
          align-items: center;
          display: flex;
          color: ${colors.darkPrimary};
          width: 100%;
        }
        .title h1 {
          font-size: 24px;
          margin: 6px 0 0 0;
        }
        .title p {
          font-size: 14px;
          margin: 0;
        }
        .row {
          display: flex;
          justify-content: space-between;
        }
        .end {
          align-items: flex-end;
        }
        .padded {
          padding-bottom: 1rem;
        }
        .row.top-gap {
          margin-top: 2.5rem;
        }
        .heading {
          color: ${colors.blueGrey};
          font-size: 14px;
          margin-bottom: 0.5rem;
        }
        .heading-blue {
          color: ${colors.bluePrimary};
        }
        .no-appointments {
          align-items: center;
          box-sizing: border-box;
          display: none;
          flex-direction: column;
          flex-grow: 1;
          height: 100%;
          justify-content: center;
          position: relative;
          text-align: center;
        }
        .no-appointments-visible {
          display: flex;
        }
        .no-appointments h3 {
          color: ${colors.darkPrimary};
          font-size: 18px;
          line-height: 22px;
          margin: 2rem 0 0;
        }
        .no-appointments p {
          color: ${colors.blueGrey};
          margin: 0.5rem 0 0;
        }
        .no-appointments a {
          align-items: center;
          color: ${colors.bluePrimary};
          cursor: pointer;
          display: flex;
          font-weight: bold;
          margin-top: 1rem;
          text-decoration: none;
        }
        .no-appointments a img{
          margin-right: 0.5rem;
        }
        .wave {
          position: absolute;
          bottom: -1.5rem;
          left: -3rem;
          pointer-events: none;
          right: 0;
          width: calc(100% + 6rem);
        }
        .search-container :global(.search) {
          width: 528px;
        }
        .table-container {
          display: block;
          height: 100%;
          overflow: none;
          position: relative;
        }
        .table-container :global(.table) {
          margin-top: 0;
        }
        .table-container-visible {
          display: block;
        }
        .loading-overlay {
          align-items: center;
          background-color: rgba(245, 248, 251, 0.6);
          bottom: 0;
          border-radius: 8px;
          display: none;
          justify-content: center;
          left: -1rem;
          position: absolute;
          right: -1rem;
          top: 0.5rem;
        }
        .loading-overlay-visible {
          display: flex;
        }
        .loading-overlay :global(.loader) {
          border-color: rgba(28, 37, 44, 0.2);
          border-left-color: ${colors.darkPrimary};
          border-width: 0.25rem;
          display: flex;
          height: 1.5rem;
          width: 1.5rem;
        }
        a {
          text-decoration: none;
        }
        .select-actions {
          display: flex;
          margin-left: -1rem;
        }
        .select-actions :global(.white-action) {
          font-size: 0.875rem;
          margin: 0 0 0 1rem; 
          width: auto;
        }
        .select-actions :global(.icon) {
          left: 1rem !important;
        }
        .download {
          margin-right: 1rem;
          width: 3rem;
        }

        .centered {
            position: relative;
            left: 50%;
            transform: translateX(-50%);
            width: ${layout.listCardWidth};
          }
        `}</style>
      </div>
    </NoSSR>
  );
}

export default AppointmentList;

export const Route = createFileRoute('/customerzone/appointmentlist')({ component: AppointmentList })
